import { getEnv } from '../../config/Env';
import { baseQueryWithReauth, isJsonContentType, prepareHeaders, providesList } from '../api';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseUrl = getEnv('FILE_API_URL');

const mediaApiQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders,
    isJsonContentType,
    responseHandler: 'content-type',
});

export const mediaApi = createApi({
    reducerPath: 'mediaApi',
    baseQuery: (args, api, extraOptions) => baseQueryWithReauth(args, api, extraOptions, mediaApiQuery),
    tagTypes: ['Upload', 'PrivateUpload', 'Folder'],
    endpoints: (builder) => ({
        getFile: builder.query({
            query: (uri) => ({
                url: uri,
            }),
            providesTags: (result) => (result ? [{ type: 'Upload', id: result['@id'] }] : []),
        }),
        updateFile: builder.mutation({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PATCH',
                headers: { 'Content-Type': 'application/merge-patch+json' },
                body,
            }),
            invalidatesTags: (result, error, { uri }) => [{ type: 'Upload', id: uri }],
        }),
        deleteFile: builder.mutation({
            query: (uri) => ({
                url: uri,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Upload', id: arg }],
        }),
        deleteFolder: builder.mutation({
            query: (uri) => ({
                url: uri,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Folder', id: arg }],
        }),
        getUploads: builder.query({
            query: (organisationId) => ({
                url: '/api/uploads',
                params: {
                    organisationId,
                },
            }),
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'Upload', '@id'),
        }),
        getFolders: builder.query({
            query: (organisationId) => ({
                url: '/api/folders',
                params: {
                    organisationId,
                },
            }),
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'Folder', '@id'),
        }),
        updateFolder: builder.mutation({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PATCH',
                headers: { 'Content-Type': 'application/merge-patch+json' },
                body,
            }),
            invalidatesTags: (result, error, { uri }) => [{ type: 'Folder', id: uri }],
        }),
        createFolder: builder.mutation({
            query: (body) => ({
                url: '/api/folders',
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, { uri }) => [{ type: 'Folder', id: 'LIST' }],
        }),
        uploadFile: builder.mutation({
            query: ({ file, organisationId, folderId, url }) => {
                let body = {};

                if (file) {
                    body = new FormData();
                    body.append('file', file);
                    body.append('organisationId', organisationId);
                    body.append('folder', folderId);
                }

                if (!file && url) {
                    body = {
                        url,
                        organisationId,
                        folderId,
                    };
                }

                return {
                    url: '/api/uploads',
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: (result, error, arg) => [{ type: 'Upload', id: 'LIST' }],
        }),
        uploadPrivateFile: builder.mutation({
            query: ({ file, organisationId }) => {
                const body = new FormData();
                body.append('file', file);
                body.append('organisationId', organisationId);

                return {
                    url: '/api/private_uploads',
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: () => [{ type: 'PrivateUpload', id: 'LIST' }],
        }),
    }),
});

export const {
    useGetFileQuery,
    useUpdateFileMutation,
    useDeleteFileMutation,
    useGetUploadsQuery,
    useUploadFileMutation,
    useUploadPrivateFileMutation,
    useGetFoldersQuery,
    useUpdateFolderMutation,
    useCreateFolderMutation,
    useDeleteFolderMutation,
} = mediaApi;
